import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import dayjs from 'dayjs';
import type { DateRange } from 'react-day-picker';
import { Link2, Settings2, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { CompanyEditModal } from '@/components/companies/CompanyEditModal';
import { HeaderActions } from '@/components/layout/AppShell';
import { MBankExportView } from '@/components/export/MBankExportView';
import { InvoiceFilterBar } from './InvoiceFilterBar';
import { InvoiceTable } from './InvoiceTable';
import { BulkActionsBar } from './BulkActionsBar';
import { UploadInvoiceDialog } from './UploadInvoiceDialog';
import { MatchPaymentDialog } from './MatchPaymentDialog';
import { BulkTagsDialog } from './BulkTagsDialog';
import { BulkAssignDialog } from './BulkAssignDialog';
import { BulkProjectDialog, NO_PROJECT } from './BulkProjectDialog';
import { AssignProjectDialog } from './AssignProjectDialog';
import { downloadBlob, sanitizeFileNamePart } from '@/utils/download';
import { t } from '@/i18n';
import {
  useInvoicesApi,
  useTagsApi,
  useCompaniesApi,
  useExportsApi,
  useUploadApi,
  useProjectsApi,
  SessionExpiredError,
} from '@/services/api';
import { useAuth } from '@/hooks/useAuth';
import { useInvoiceFilters } from '@/hooks/useInvoiceFilters';
import { useSelectedInvoiceItems } from '@/hooks/useSelectedInvoiceItems';
import type {
  InvoiceListItem,
  Tag as TagType,
  InvoiceStatus,
  CompanyListItem,
  ProjectOption,
} from '@ip/shared-types';

interface InvoiceListProps {
  companyNip: string;
  companyName: string;
  onCompanyNameLoad?: (name: string) => void;
  onInvoiceSelect?: (invoiceId: string) => void;
  refreshKey?: number;
}

export function InvoiceList({
  companyNip,
  companyName,
  onCompanyNameLoad,
  onInvoiceSelect,
  refreshKey,
}: InvoiceListProps) {
  // State — 1:1 z oryginału
  const [invoices, setInvoices] = useState<InvoiceListItem[]>([]);
  const [tags, setTags] = useState<TagType[]>([]);
  const [projects, setProjects] = useState<ProjectOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);
  const [companyEditOpen, setCompanyEditOpen] = useState(false);
  const [mbankExportOpen, setMbankExportOpen] = useState(false);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [matchPaymentOpen, setMatchPaymentOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadDescription, setUploadDescription] = useState('');
  const [uploading, setUploading] = useState(false);
  const [bulkTagModalOpen, setBulkTagModalOpen] = useState(false);
  const [bulkTagsToAdd, setBulkTagsToAdd] = useState<string[]>([]);
  const [bulkUpdating, setBulkUpdating] = useState(false);
  const [pdfLoading, setPdfLoading] = useState<Record<string, boolean>>({});
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [assignTargetNip, setAssignTargetNip] = useState<string | null>(null);
  const [bulkProjectModalOpen, setBulkProjectModalOpen] = useState(false);
  // '' = nothing chosen, NO_PROJECT = clear the assignment, else a project id.
  const [bulkProjectSelection, setBulkProjectSelection] = useState('');
  // Single-row "Przypisz do projektu" action — target invoice + in-flight flag.
  const [projectTarget, setProjectTarget] = useState<InvoiceListItem | null>(null);
  const [projectAssigning, setProjectAssigning] = useState(false);
  const [assignableCompanies, setAssignableCompanies] = useState<CompanyListItem[]>([]);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; invoiceNumber: string | null } | null>(null);
  const [bulkDeleteConfirmOpen, setBulkDeleteConfirmOpen] = useState(false);
  const loadSeq = useRef(0);

  const {
    search, setSearch,
    mppFilter, setMppFilter,
    dateType, setDateType,
    dateRange, setDateRange,
    selectedTags, setSelectedTags,
    statusFilter, setStatusFilter,
    sourceFilter, setSourceFilter,
    noNotesOnly, setNoNotesOnly,
    reimbursementFilter, setReimbursementFilter,
    projectFilter, setProjectFilter,
    page, setPage,
    pageSize, setPageSize,
    sortBy,
    sortOrder,
    updateParams,
    clearAllFilters,
    hasActiveFilters,
  } = useInvoiceFilters();

  const { getInvoices, updateInvoiceTags, updateInvoiceStatus, deleteInvoice, bulkUpdateTags, bulkUpdateStatus, bulkDeleteInvoices, bulkAssignProject, getKsefPdf, downloadInvoicePdf, assignInvoices } = useInvoicesApi();
  const { getTags } = useTagsApi();
  const { listProjectOptions, assignInvoiceProject } = useProjectsApi();
  const { getCompanies } = useCompaniesApi();
  const { generatePdfZipExport } = useExportsApi();
  const { uploadInvoice } = useUploadApi();
  const { isAdmin } = useAuth();

  // Load company name + assignable companies — 1:1
  useEffect(() => {
    if (!companyNip) return;
    async function loadCompanyData() {
      try {
        const companies = await getCompanies();
        if (onCompanyNameLoad) {
          const company = companies.find((c) => c.nip === companyNip);
          if (company) {
            onCompanyNameLoad(
              companyNip === 'other' ? t('navigation.unassigned') : company.name,
            );
          }
        }
        setAssignableCompanies(companies.filter((c: CompanyListItem) => c.nip !== 'other' && c.nip !== companyNip));
      } catch (error) {
        console.error('Failed to load companies:', error);
      }
    }
    loadCompanyData();
  }, [companyNip, getCompanies, onCompanyNameLoad]);

  // Load tags — 1:1
  useEffect(() => {
    if (!companyNip) return;
    async function loadTags() {
      try {
        const data = await getTags(companyNip, 'expense');
        setTags(data);
      } catch (error) {
        console.error('Failed to load tags:', error);
      }
    }
    loadTags();
  }, [companyNip, getTags]);

  // Load projects for the filter (Task B3). A 403 (tag-restricted user) or
  // any failure just leaves the filter hidden — the list itself still works.
  useEffect(() => {
    if (!companyNip) return;
    let mounted = true;
    setProjects([]);
    listProjectOptions(companyNip)
      .then((data) => {
        if (mounted) setProjects(data);
      })
      .catch(() => {
        /* no project-list access — hide the filter */
      });
    return () => {
      mounted = false;
    };
  }, [companyNip, listProjectOptions]);

  // Load invoices — 1:1
  const loadInvoices = useCallback(async () => {
    if (!companyNip) return;
    const seq = ++loadSeq.current;
    try {
      setLoading(true);
      const result = await getInvoices(companyNip, {
        search: search || undefined,
        mppFilter: mppFilter === 'all' ? undefined : mppFilter,
        dateType,
        dateFrom: dateRange?.[0]?.format('YYYY-MM-DD'),
        dateTo: dateRange?.[1]?.format('YYYY-MM-DD'),
        tagIds: selectedTags.length > 0 ? selectedTags : undefined,
        status: statusFilter === 'all' ? undefined : statusFilter,
        source: sourceFilter === 'all' ? undefined : sourceFilter,
        // "Bez opisu" — hasNotes=false means invoices WITHOUT notes.
        hasNotes: noNotesOnly ? false : undefined,
        projectId: projectFilter === 'all' ? undefined : projectFilter,
        reimbursementFilter: reimbursementFilter === 'all' ? undefined : reimbursementFilter,
        page,
        pageSize,
        sortBy,
        sortOrder,
      });
      if (seq === loadSeq.current) {
        setInvoices(result.items);
        setTotal(result.total);
      }
    } catch {
      toast.error(t('errors.loadingFailed'));
    } finally {
      if (seq === loadSeq.current) setLoading(false);
    }
  }, [companyNip, search, mppFilter, dateType, dateRange, selectedTags, statusFilter, sourceFilter, noNotesOnly, reimbursementFilter, projectFilter, page, pageSize, sortBy, sortOrder, getInvoices]);

  useEffect(() => {
    loadInvoices();
  }, [loadInvoices, refreshKey]);

  // Selection must not leak between companies.
  useEffect(() => {
    setSelectedRowKeys([]);
  }, [companyNip]);

  // Inline tag update — 1:1
  const handleTagChange = useCallback(
    async (invoiceId: string, currentTags: string[], tagId: string, checked: boolean) => {
      const newTags = checked
        ? [...currentTags, tagId]
        : currentTags.filter((id) => id !== tagId);
      try {
        await updateInvoiceTags(companyNip, invoiceId, newTags);
        setInvoices((prev) =>
          prev.map((inv) => (inv.id === invoiceId ? { ...inv, tags: newTags } : inv)),
        );
      } catch (error) {
        toast.error(t('invoices.tagsEditor.updateError'));
        console.error(error);
      }
    },
    [companyNip, updateInvoiceTags],
  );

  // Single-row status change (actions menu) — mirrors the bulk status action.
  const handleRowStatusChange = useCallback(
    async (invoiceId: string, status: InvoiceStatus) => {
      try {
        await updateInvoiceStatus(companyNip, invoiceId, status);
        toast.success(
          t('invoices.bulk.statusSuccess', { count: 1, status: t(`invoices.status.${status}`) }),
        );
        loadInvoices();
      } catch {
        toast.error(t('invoices.bulk.statusError'));
      }
    },
    [companyNip, updateInvoiceStatus, loadInvoices],
  );

  // Delete — potwierdzenie przez AlertDialog (stan deleteTarget)
  const confirmDelete = useCallback(async () => {
    if (!deleteTarget) return;
    try {
      await deleteInvoice(companyNip, deleteTarget.id);
      toast.success(t('invoices.delete.success'));
      setInvoices((prev) => prev.filter((inv) => inv.id !== deleteTarget.id));
      setTotal((prev) => prev - 1);
    } catch {
      toast.error(t('invoices.delete.error'));
    } finally {
      setDeleteTarget(null);
    }
  }, [deleteTarget, companyNip, deleteInvoice]);

  // Upload — 1:1
  const handleUpload = useCallback(async () => {
    if (!uploadFile) return;
    setUploading(true);
    try {
      const result = await uploadInvoice(uploadFile, {
        nip: companyNip,
        description: uploadDescription || undefined,
      });
      if (result.isInvoice) {
        toast.success(t('invoices.upload.success'));
        loadInvoices();
      } else {
        toast.warning(t('invoices.upload.notInvoice'));
      }
      setUploadFile(null);
      setUploadDescription('');
      setUploadModalOpen(false);
    } catch {
      toast.error(t('invoices.upload.error'));
    } finally {
      setUploading(false);
    }
  }, [uploadFile, uploadInvoice, companyNip, uploadDescription, loadInvoices]);

  // Bulk status — 1:1
  const handleBulkStatusChange = useCallback(async (status: InvoiceStatus) => {
    if (selectedRowKeys.length === 0) return;
    setBulkUpdating(true);
    try {
      await bulkUpdateStatus(companyNip, selectedRowKeys, status);
      toast.success(t('invoices.bulk.statusSuccess', { count: selectedRowKeys.length, status: t(`invoices.status.${status}`) }));
      setSelectedRowKeys([]);
      loadInvoices();
    } catch {
      toast.error(t('invoices.bulk.statusError'));
    } finally {
      setBulkUpdating(false);
    }
  }, [selectedRowKeys, companyNip, bulkUpdateStatus, loadInvoices]);

  // Bulk tags — 1:1
  const handleBulkAddTags = useCallback(async () => {
    if (selectedRowKeys.length === 0 || bulkTagsToAdd.length === 0) return;
    setBulkUpdating(true);
    try {
      await bulkUpdateTags(companyNip, selectedRowKeys, bulkTagsToAdd, undefined);
      toast.success(t('invoices.bulk.tagsSuccess', { count: selectedRowKeys.length }));
      setBulkTagModalOpen(false);
      setBulkTagsToAdd([]);
      setSelectedRowKeys([]);
      loadInvoices();
    } catch {
      toast.error(t('invoices.bulk.tagsError'));
    } finally {
      setBulkUpdating(false);
    }
  }, [selectedRowKeys, bulkTagsToAdd, companyNip, bulkUpdateTags, loadInvoices]);

  // Bulk assign — 1:1
  const handleBulkAssign = useCallback(async () => {
    if (selectedRowKeys.length === 0 || !assignTargetNip) return;
    setBulkUpdating(true);
    try {
      const result = await assignInvoices(companyNip, selectedRowKeys, assignTargetNip);
      toast.success(t('invoices.bulk.assignSuccess', { count: result.assigned }));
      setAssignModalOpen(false);
      setAssignTargetNip(null);
      setSelectedRowKeys([]);
      loadInvoices();
    } catch {
      toast.error(t('invoices.bulk.assignError'));
    } finally {
      setBulkUpdating(false);
    }
  }, [selectedRowKeys, assignTargetNip, assignInvoices, companyNip, loadInvoices]);

  // Bulk project assignment (manual provenance — the rule engine never
  // touches these afterwards). NO_PROJECT clears the assignment.
  const handleBulkAssignProject = useCallback(async () => {
    if (selectedRowKeys.length === 0 || !bulkProjectSelection) return;
    setBulkUpdating(true);
    try {
      const projectId = bulkProjectSelection === NO_PROJECT ? null : bulkProjectSelection;
      const result = await bulkAssignProject(companyNip, selectedRowKeys, projectId);
      toast.success(t('invoices.bulk.projectSuccess', { count: result.updated }));
      setBulkProjectModalOpen(false);
      setBulkProjectSelection('');
      setSelectedRowKeys([]);
      loadInvoices();
    } catch {
      toast.error(t('invoices.bulk.projectError'));
    } finally {
      setBulkUpdating(false);
    }
  }, [selectedRowKeys, bulkProjectSelection, companyNip, bulkAssignProject, loadInvoices]);

  // Single-invoice project assignment (row action) — whole-invoice endpoint,
  // MANUAL provenance; null clears. On success the row is patched in place
  // (no full reload), mirroring what the backend would return.
  const handleAssignProjectSingle = useCallback(async (projectId: string | null) => {
    if (!projectTarget) return;
    setProjectAssigning(true);
    try {
      await assignInvoiceProject(companyNip, projectTarget.id, projectId);
      const projectName = projectId
        ? (projects.find((p) => p.id === projectId)?.name ?? null)
        : null;
      setInvoices((prev) =>
        prev.map((inv) =>
          inv.id === projectTarget.id
            ? { ...inv, projectId, projectName, projectAssignedBy: projectId ? 'manual' : null }
            : inv,
        ),
      );
      toast.success(t('invoices.assignProject.success'));
      setProjectTarget(null);
    } catch {
      toast.error(t('invoices.assignProject.error'));
    } finally {
      setProjectAssigning(false);
    }
  }, [projectTarget, companyNip, projects, assignInvoiceProject]);

  // Bulk delete — admin-only, works in every company incl. 'other'
  const handleBulkDelete = useCallback(async () => {
    if (selectedRowKeys.length === 0) return;
    setBulkUpdating(true);
    try {
      const result = await bulkDeleteInvoices(companyNip, selectedRowKeys);
      toast.success(t('invoices.bulk.deleteSuccess', { count: result.deleted }));
      setBulkDeleteConfirmOpen(false);
      setSelectedRowKeys([]);
      loadInvoices();
    } catch {
      toast.error(t('invoices.bulk.deleteError'));
    } finally {
      setBulkUpdating(false);
    }
  }, [selectedRowKeys, companyNip, bulkDeleteInvoices, loadInvoices]);

  // PDF "open" — SAVES the file now: the presigned-URL new-tab flow died on
  // hosts where window.open is silently blocked (see utils/download.ts).
  const handlePdfOpen = useCallback(async (record: InvoiceListItem) => {
    setPdfLoading((prev) => ({ ...prev, [record.id]: true }));
    // KSeF-synced invoices store fileName like ksef_<ksefNumber>.xml — that's
    // the source XML, not the rendered PDF getKsefPdf() returns. Always name
    // the saved PDF from the invoice number instead of trusting record.fileName.
    const pdfFileName = `FV-${sanitizeFileNamePart(record.invoiceNumber || record.id)}.pdf`;
    try {
      if (record.source === 'ksef') {
        const blob = await getKsefPdf(companyNip, record.id);
        downloadBlob(blob, pdfFileName);
      } else {
        // downloadInvoicePdf toasts its own failure; only session expiry
        // (handled by the global modal) reaches this catch from it.
        const fileName = record.fileName || pdfFileName;
        await downloadInvoicePdf(companyNip, record.id, fileName);
      }
    } catch (error) {
      if (!(error instanceof SessionExpiredError)) toast.error(t('errors.pdfFailed'));
    } finally {
      setPdfLoading((prev) => ({ ...prev, [record.id]: false }));
    }
  }, [companyNip, getKsefPdf, downloadInvoicePdf]);

  // Export — 1:1 (toast zamiast message)
  const handleExport = useCallback(async (key: 'pdf' | 'mbank') => {
    if (key === 'mbank') {
      if (selectedRowKeys.length === 0) {
        toast.warning(t('invoices.export.selectToExport'));
        return;
      }
      setMbankExportOpen(true);
    } else if (key === 'pdf') {
      if (selectedRowKeys.length === 0) {
        toast.warning(t('invoices.export.selectToExport'));
        return;
      }
      const toastId = toast.loading(t('invoices.export.pdfZipLoading'));
      try {
        const blob = await generatePdfZipExport(companyNip, selectedRowKeys);
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        const dateStr = new Date().toISOString().split('T')[0];
        link.download = `invoices_${companyNip}_${dateStr}.zip`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        toast.success(t('invoices.export.pdfZipSuccess'), { id: toastId });
      } catch (error) {
        console.error('PDF export failed:', error);
        toast.error(t('invoices.export.pdfZipError'), { id: toastId });
      }
    }
  }, [selectedRowKeys, companyNip, generatePdfZipExport]);

  // Cross-page row data for the footer sum AND mBank/PDF export — selection
  // spans pages, so resolve ids against the cross-page cache (the current
  // page's `invoices` state alone would silently drop off-page selections).
  const selectedItems = useSelectedInvoiceItems(invoices, selectedRowKeys);

  // Cykl sortowania — parytet z poprzednim UI (asc → desc → reset createdAt/desc)
  const handleSortToggle = useCallback(
    (columnKey: 'issueDate' | 'createdAt') => {
      let newSortBy: string;
      let newSortOrder: 'asc' | 'desc';
      if (sortBy !== columnKey) {
        newSortBy = columnKey;
        newSortOrder = 'asc';
      } else if (sortOrder === 'asc') {
        newSortBy = columnKey;
        newSortOrder = 'desc';
      } else {
        newSortBy = 'createdAt';
        newSortOrder = 'desc';
      }
      updateParams({
        sb: newSortBy === 'createdAt' ? null : newSortBy,
        so: newSortOrder === 'desc' ? null : newSortOrder,
      });
    },
    [sortBy, sortOrder, updateParams],
  );

  // Konwersja Dayjs <-> DateRange na granicy pickera
  const pickerRange: DateRange | undefined = useMemo(() => {
    if (!dateRange) return undefined;
    return {
      from: dateRange[0]?.toDate(),
      to: dateRange[1]?.toDate(),
    };
  }, [dateRange]);

  const handleDateRangeChange = useCallback(
    (range: DateRange | undefined) => {
      if (!range?.from && !range?.to) {
        setDateRange(null);
      } else {
        setDateRange([
          range?.from ? dayjs(range.from) : null,
          range?.to ? dayjs(range.to) : null,
        ]);
      }
    },
    [setDateRange],
  );

  const toggleTagFilter = useCallback(
    (tagId: string) => {
      setSelectedTags((prev) =>
        prev.includes(tagId) ? prev.filter((id) => id !== tagId) : [...prev, tagId],
      );
    },
    [setSelectedTags],
  );

  if (mbankExportOpen) {
    return (
      <MBankExportView
        companyNip={companyNip}
        companyName={companyName}
        selectedInvoices={selectedItems}
        onClose={() => {
          setMbankExportOpen(false);
          setSelectedRowKeys([]);
          loadInvoices();
        }}
      />
    );
  }

  return (
    <div className="flex flex-col gap-2">
      {companyNip !== 'other' && (
        <HeaderActions>
          <Button variant="outline" size="sm" onClick={() => setMatchPaymentOpen(true)}>
            <Link2 className="size-4" />
            {t('invoices.combinedPayment.button')}
          </Button>
          <Button variant="outline" size="sm" onClick={() => setUploadModalOpen(true)}>
            <Upload className="size-4" />
            {t('invoices.upload.button')}
          </Button>
          <Button variant="outline" size="sm" onClick={() => setCompanyEditOpen(true)}>
            <Settings2 className="size-4" />
            Edit Company
          </Button>
        </HeaderActions>
      )}

      <InvoiceFilterBar
        search={search}
        onSearchChange={setSearch}
        mppFilter={mppFilter}
        onMppChange={setMppFilter}
        dateType={dateType}
        onDateTypeChange={setDateType}
        dateRange={pickerRange}
        onDateRangeChange={handleDateRangeChange}
        statusFilter={statusFilter}
        onStatusChange={setStatusFilter}
        sourceFilter={sourceFilter}
        onSourceChange={setSourceFilter}
        noNotesOnly={noNotesOnly}
        onNoNotesChange={setNoNotesOnly}
        reimbursementFilter={reimbursementFilter}
        onReimbursementChange={setReimbursementFilter}
        projects={projects}
        projectFilter={projectFilter}
        onProjectChange={setProjectFilter}
        tags={tags}
        selectedTags={selectedTags}
        onTagToggle={toggleTagFilter}
        hasActiveFilters={hasActiveFilters}
        onClearFilters={clearAllFilters}
        onRefresh={loadInvoices}
        onExport={handleExport}
      />

      {selectedRowKeys.length > 0 && (
        <BulkActionsBar
          count={selectedRowKeys.length}
          updating={bulkUpdating}
          canAssign={assignableCompanies.length > 0}
          canAssignProject={projects.length > 0}
          canDelete={isAdmin}
          onAddTags={() => setBulkTagModalOpen(true)}
          onMarkPaid={() => handleBulkStatusChange('paid')}
          onMarkUnpaid={() => handleBulkStatusChange('unpaid')}
          onMarkExported={() => handleBulkStatusChange('exported')}
          onAssign={() => setAssignModalOpen(true)}
          onAssignProject={() => setBulkProjectModalOpen(true)}
          onDelete={() => setBulkDeleteConfirmOpen(true)}
        />
      )}

      <InvoiceTable
        invoices={invoices}
        companyNip={companyNip}
        loading={loading}
        total={total}
        page={page}
        pageSize={pageSize}
        sortBy={sortBy}
        sortOrder={sortOrder}
        selectedIds={selectedRowKeys}
        onSelectionChange={setSelectedRowKeys}
        selectedItems={selectedItems}
        tags={tags}
        pdfLoading={pdfLoading}
        onPdfOpen={handlePdfOpen}
        onInvoiceSelect={onInvoiceSelect}
        onTagChange={handleTagChange}
        onStatusChange={handleRowStatusChange}
        onDelete={(id, invoiceNumber) => setDeleteTarget({ id, invoiceNumber })}
        canAssignProject={projects.length > 0}
        onAssignProject={setProjectTarget}
        isAdmin={isAdmin}
        onSortToggle={handleSortToggle}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
      />

      <CompanyEditModal
        open={companyEditOpen}
        companyNip={companyNip}
        companyName={companyName}
        onClose={() => setCompanyEditOpen(false)}
        onSaved={() => {
          loadInvoices();
        }}
      />

      <MatchPaymentDialog
        companyNip={companyNip}
        open={matchPaymentOpen}
        onOpenChange={setMatchPaymentOpen}
        onLinked={loadInvoices}
      />

      <UploadInvoiceDialog
        open={uploadModalOpen}
        onOpenChange={(open) => {
          setUploadModalOpen(open);
          if (!open) {
            setUploadFile(null);
            setUploadDescription('');
          }
        }}
        file={uploadFile}
        onFileChange={setUploadFile}
        description={uploadDescription}
        onDescriptionChange={setUploadDescription}
        uploading={uploading}
        onSubmit={handleUpload}
      />

      <BulkTagsDialog
        open={bulkTagModalOpen}
        onOpenChange={(open) => {
          setBulkTagModalOpen(open);
          if (!open) setBulkTagsToAdd([]);
        }}
        tags={tags}
        selectedTagIds={bulkTagsToAdd}
        onToggleTag={(tagId, checked) => {
          setBulkTagsToAdd((prev) =>
            checked ? [...prev, tagId] : prev.filter((id) => id !== tagId),
          );
        }}
        count={selectedRowKeys.length}
        updating={bulkUpdating}
        onSubmit={handleBulkAddTags}
      />

      <BulkAssignDialog
        open={assignModalOpen}
        onOpenChange={(open) => {
          setAssignModalOpen(open);
          if (!open) setAssignTargetNip(null);
        }}
        companies={assignableCompanies}
        targetNip={assignTargetNip}
        onTargetChange={setAssignTargetNip}
        count={selectedRowKeys.length}
        updating={bulkUpdating}
        onSubmit={handleBulkAssign}
      />

      <BulkProjectDialog
        open={bulkProjectModalOpen}
        onOpenChange={(open) => {
          setBulkProjectModalOpen(open);
          if (!open) setBulkProjectSelection('');
        }}
        projects={projects}
        selection={bulkProjectSelection}
        onSelectionChange={setBulkProjectSelection}
        count={selectedRowKeys.length}
        updating={bulkUpdating}
        onSubmit={handleBulkAssignProject}
      />

      <AssignProjectDialog
        target={projectTarget}
        projects={projects}
        updating={projectAssigning}
        onOpenChange={(open) => {
          if (!open) setProjectTarget(null);
        }}
        onSubmit={handleAssignProjectSingle}
      />

      <AlertDialog open={deleteTarget !== null} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('invoices.delete.confirmTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('invoices.delete.confirmMessage', {
                invoiceNumber: deleteTarget?.invoiceNumber || deleteTarget?.id || '',
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-white hover:bg-destructive/90"
              onClick={confirmDelete}
            >
              {t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={bulkDeleteConfirmOpen} onOpenChange={setBulkDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('invoices.bulk.deleteConfirmTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('invoices.bulkDeleteConfirm', { count: selectedRowKeys.length })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={bulkUpdating}>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-white hover:bg-destructive/90"
              disabled={bulkUpdating}
              onClick={handleBulkDelete}
            >
              {t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
